import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Microscope, X } from 'lucide-react';
import api from '../api';

interface ProviderEstimate {
  creditRange: [number, number];
  timeRangeMinutes: [number, number];
  available: boolean;
}

interface Props {
  runId: string;
  projectId: string;
  onApprove: () => void;
  onSkip: () => void;
}

interface QualifiedEstimate {
  projectId: string;
  runId: string;
  credits: [number, number];
  minutes: [number, number];
}

function validRange(range: unknown): range is [number, number] {
  return Array.isArray(range) && range.length === 2 && range.every((value: unknown) => typeof value === 'number' && Number.isFinite(value) && value >= 0) && range[0] <= range[1];
}

function admissionMessage(error: unknown): string {
  return error instanceof Error && error.message === 'sync_spend_not_allowed'
    ? 'Review approval is unavailable for your current credit or subscription status. You can skip this review.'
    : 'Review estimate unavailable. We could not confirm the cost and available credits. You can skip and try again on a later sync.';
}

/** Approval is for the architecture review, not a provider/model choice. MC owns routing. */
export function ArchReviewModal({ runId, projectId, onApprove, onSkip }: Props) {
  const [estimate, setEstimate] = useState<QualifiedEstimate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const generation = useRef(0);

  useEffect(() => {
    generation.current += 1;
    let active = true;
    setEstimate(null);
    setError(null);
    setLoading(true);
    setSubmitting(false);
    api.syncAuditEstimate(projectId).then((data) => {
      if (!active) return;
      // Balance is display-only (it may be null when the wallet is unavailable);
      // the server's approval admission is the authoritative spend gate.
      if (!Array.isArray(data.providers)) throw new Error('unqualified estimate');
      const available = data.providers.filter((provider: ProviderEstimate) => provider.available);
      if (available.length === 0 || available.some((provider: ProviderEstimate) => !validRange(provider.creditRange) || !validRange(provider.timeRangeMinutes))) throw new Error('unqualified estimate');
      setEstimate({
        projectId,
        runId,
        credits: [Math.min(...available.map((provider: ProviderEstimate) => provider.creditRange[0])), Math.max(...available.map((provider: ProviderEstimate) => provider.creditRange[1]))],
        minutes: [Math.min(...available.map((provider: ProviderEstimate) => provider.timeRangeMinutes[0])), Math.max(...available.map((provider: ProviderEstimate) => provider.timeRangeMinutes[1]))],
      });
    }).catch((failure: unknown) => {
      if (!active) return;
      setEstimate(null);
      setError(admissionMessage(failure));
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; generation.current += 1; };
  }, [projectId, runId]);

  const currentEstimate = estimate?.projectId === projectId && estimate.runId === runId ? estimate : null;

  const approve = async () => {
    if (loading || submitting || !currentEstimate) return;
    const operationGeneration = generation.current;
    setSubmitting(true);
    try {
      await api.syncAuditApprove(runId, true);
      if (generation.current !== operationGeneration) return;
      onApprove();
    } catch (failure: unknown) {
      if (generation.current !== operationGeneration) return;
      setEstimate(null);
      setError(admissionMessage(failure));
      setSubmitting(false);
    }
  };

  const skip = async () => {
    if (submitting) return;
    const operationGeneration = generation.current;
    setSubmitting(true);
    try {
      await api.syncAuditApprove(runId, false);
      if (generation.current !== operationGeneration) return;
      onSkip();
    } catch {
      if (generation.current !== operationGeneration) return;
      setSubmitting(false);
    }
  };

  return <div role="dialog" aria-modal="true" aria-label="Architecture review approval" style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' }}>
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', width: '100%', maxWidth: 560, padding: 24, margin: '0 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 8 }}><Microscope size={20} /> Architecture Review</h2>
        <button aria-label="Skip architecture review" onClick={skip} disabled={submitting} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', padding: 4 }}><X size={18} /></button>
      </div>
      {loading ? <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}><Loader2 style={{ animation: 'spin 1s linear infinite', color: 'var(--orange)' }} size={24} /></div> : <>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginBottom: 16 }}>Run an independent architecture review of your code. It looks for structural risks (missing tests, no CI, security boundaries) and adds what it finds to your issue list. Two reviewers check each other; we choose the models for you.</p>
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)', marginBottom: 12 }}>Skip finishes this sync without the review. You can run it on a later sync.</p>
        {error && <p role="alert" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginBottom: 16 }}>{error}</p>}
        {currentEstimate && <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)', marginBottom: 16 }}>Estimated cost: {currentEstimate.credits[0]}–{currentEstimate.credits[1]} credits from your balance · {currentEstimate.minutes[0]}–{currentEstimate.minutes[1]} min.</p>}
        <div style={{ display: 'flex', gap: 12 }}>
          <button onClick={skip} disabled={submitting} className="btn btn-sm" style={{ flex: 1 }}>Skip review</button>
          <button onClick={approve} disabled={submitting || loading || !currentEstimate} className="btn btn-primary btn-sm" style={{ flex: 1 }}>{submitting ? <Loader2 aria-label="Submitting approval" style={{ animation: 'spin 1s linear infinite', display: 'block', margin: '0 auto' }} size={16} /> : 'Approve review'}</button>
        </div>
      </>}
    </div>
  </div>;
}
